import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, authStatus, sessionError } = useAuth();

  // ── Still doing the initial session check on mount ───────────────────────
  // Show a spinner. This only lasts for the first profile fetch after a
  // page load/refresh — it will NOT hang here because AuthContext guarantees
  // authStatus always resolves to 'idle'.
  if (authStatus === "initializing" || authStatus === "loading") {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold text-orange-600 mb-2">
            Loading...
          </div>
          {authStatus === "loading" && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Verifying your session
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Session expired while the user was on this page ──────────────────────
  // authStatus is 'idle' but user is null AND there's a sessionError.
  // Show a dedicated prompt instead of silently bouncing to /login,
  // which was causing the invisible redirect + frozen login page bug.
  if (!user && sessionError) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-8 max-w-md text-center">
          <div className="text-4xl mb-4">⏱️</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Session Expired
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {sessionError}
          </p>
          <a
            href="/login"
            className="inline-block bg-orange-600 text-white px-8 py-3 rounded-xl
                            font-bold hover:bg-orange-700 transition"
          >
            Log in again
          </a>
        </div>
      </div>
    );
  }

  // ── Not logged in at all ─────────────────────────────────────────────────
  // authStatus is 'idle', user is null, no sessionError (fresh visit).
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // ── Logged in but wrong role ─────────────────────────────────────────────
  // user.roles is an array of all roles the user holds (multi-role support).
  // Falls back to checking user.role for any legacy callers.
  const userRoles = user.roles ?? (user.role ? [user.role] : []);
  if (
    allowedRoles.length > 0 &&
    !allowedRoles.some((r) => userRoles.includes(r))
  ) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-8 max-w-md text-center">
          <div className="text-4xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-700 dark:text-gray-300">
            You don't have permission to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-6 bg-orange-600 text-white px-6 py-2 rounded-xl
                            hover:bg-orange-700 font-semibold"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
